import type { MinBuyViolation } from "@repo/shared/types";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./use-min-buy-followup-line.i18n";

/**
 * QL-398 — one display line per account-follow-up min-buy entry, worded by
 * the transaction it judges (a new buy, a top-up, or one switch pair). Used
 * by the builder's list, the row tooltip and the pair-line tooltip so the
 * wording is the same everywhere. Entries without `followup` (new advice,
 * model, agreement follow-ups) keep their own templates.
 */
export function useMinBuyFollowupLine(): (v: MinBuyViolation) => string {
  const t = useT(T);
  const localize = useLocalize();
  const { formatNumber } = useRegionalFormat();
  return (v) => {
    const kind = v.followup?.kind ?? "buy";
    const template =
      kind === "switch"
        ? t.minBuyFollowupSwitch
        : kind === "topup"
          ? t.minBuyFollowupTopup
          : t.minBuyFollowupBuy;
    return template({
      product: localize(v.productNameI18n),
      amount: formatNumber(v.amount),
      existing: formatNumber(v.followup?.existingAmount ?? 0),
      min: formatNumber(v.minBuyAmount),
    });
  };
}

/** A stable React key per entry: a product can repeat (one entry per switch pair). */
export function minBuyFollowupLineKey(v: MinBuyViolation): string {
  return v.followup?.switchPairId ? `${v.productId}:${v.followup.switchPairId}` : v.productId;
}
